import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  Shield, Zap, Heart, Headphones, Award, Users, TrendingUp, 
  ArrowRight, Star, CheckCircle, Target, Truck, Globe, Clock
} from 'lucide-react';
import { aboutContent, companyInfo, statistics, heroImages } from '../data/mock';
import lepusTruck from '../assets/images/lepus-group-truck.jpeg';

// Animations
const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1
    }
  }
};

const slideIn = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }
  }
};

// Value Card
const ValueCard = ({ value, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const IconComponent = {
    Shield, Zap, Heart, Headphones
  }[value.icon] || Shield;

  return (
    <motion.div
      ref={ref}
      variants={fadeIn}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ delay: index * 0.1 }}
      className="h-full"
    >
      <div className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#1d526d]/20 h-full flex flex-col group hover:-translate-y-1">
        <motion.div
          className="w-16 h-16 bg-gradient-to-br from-[#1d526d] to-[#2a6b89] rounded-xl flex items-center justify-center mb-6 shadow-lg"
          whileHover={{ scale: 1.1, rotate: 5 }}
          transition={{ type: "spring", stiffness: 300, damping: 20 }}
        >
          <IconComponent className="w-8 h-8 text-white" />
        </motion.div>
        
        <h3 className="text-2xl font-bold text-black mb-4 group-hover:text-[#1d526d] transition-colors duration-300">
          {value.title}
        </h3>
        
        <p className="text-gray-600 leading-relaxed text-lg flex-grow">
          {value.description}
        </p>
        
        <motion.div
          className="mt-6 flex items-center text-[#1d526d] font-bold"
          whileHover={{ x: 4 }}
          transition={{ duration: 0.3 }}
        >
          <span>Core Value</span>
          <ArrowRight className="w-4 h-4 ml-2" />
        </motion.div>
      </div>
    </motion.div>
  );
};

// Milestone Card (Timeline)
const MilestoneCard = ({ milestone, index }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });
  const isEven = index % 2 === 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, x: isEven ? -50 : 50 }}
      animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: isEven ? -50 : 50 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="relative mb-12 flex flex-col lg:flex-row items-start lg:items-center"
    >
      {/* Dot always centered on the line */}
      <div className="absolute left-2.5 lg:left-1/2 transform lg:-translate-x-1/2 w-4 h-4 bg-[#1d526d] rounded-full flex-shrink-0">
        <div className="absolute inset-0 bg-[#1d526d] rounded-full animate-ping opacity-75"></div>
      </div>

      {/* Content */}
      <div
        className={`ml-12 lg:ml-0 flex-1 ${
          isEven ? 'lg:mr-8 lg:text-right' : 'lg:ml-8 lg:text-left'
        }`}
      >
        <motion.div
          className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
          whileHover={{ scale: 1.02 }}
        >
          <div className="flex items-center mb-4">
            <div className="w-16 h-16 bg-gradient-to-r from-[#1d526d] to-[#2a6b89] rounded-full flex items-center justify-center text-white font-black text-xl mr-4">
              {milestone.year}
            </div>
            <div>
              <h3 className="text-xl font-bold text-black">{milestone.event}</h3>
              <p className="text-[#1d526d] font-bold">{milestone.achievement}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};


// Animated Counter
const AnimatedCounter = ({ number, suffix = "", precision = 0, duration = 2 }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
  const [count, setCount] = useState(0);

  React.useEffect(() => {
    if (inView) {
      let startTime = null;
      const animate = (currentTime) => {
        if (startTime === null) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
        
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        setCount(number * easeOutQuart);
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setCount(number);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [inView, number, duration]);

  return (
    <div ref={ref} className="text-4xl font-bold text-[#1d526d]">
      {count.toFixed(precision)}{suffix}
    </div>
  );
};

// Main About Component
const About = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [storyRef, storyInView] = useInView({ triggerOnce: true, threshold: 0.2 });

  const achievements = [
    { icon: Users, title: "1000+", subtitle: "Satisfied Clients", description: "Trusted by leading companies across North America" },
    { icon: Truck, title: "98.5%", subtitle: "On-Time Delivery", description: "Industry-leading reliability and performance" },
    { icon: Globe, title: "2 Countries", subtitle: "Service Coverage", description: "Comprehensive Canada and USA operations" },
    { icon: Award, title: "18+", subtitle: "Years Experience", description: "Proven track record since 2006" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Clean & Modern */}
     <section className="relative min-h-screen bg-gradient-to-br from-gray-50 to-white overflow-hidden pt-4 sm:pt-6 lg:pt-8">
        {/* Background Image */}
        <div className="absolute inset-0 opacity-15">
          <img 
            src={lepusTruck}
            alt="About Lepus Group" 
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/95"></div>
        </div>

        {/* Clean Geometric Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#1d526d]/5 to-transparent rounded-full blur-2xl"
          />
        </div>

        <div ref={heroRef} className="relative z-10 min-h-screen flex items-center py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
            <div className="text-center">
              <motion.h1
                variants={slideIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="text-5xl lg:text-6xl font-bold text-black mb-8"
              >
                About
                <span className="block text-[#1d526d] mt-2">Lepus Group</span>
              </motion.h1>
              
              <motion.p
                variants={fadeIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="text-xl text-gray-600 mb-12 max-w-4xl mx-auto leading-relaxed"
              >
                {aboutContent.mission}
              </motion.p>

              <motion.div
                variants={fadeIn}
                initial="hidden"
                animate={heroInView ? "visible" : "hidden"}
                className="flex flex-col sm:flex-row gap-6 justify-center"
              >
                <motion.a 
                  href="#story"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-[#1d526d] hover:bg-black text-white px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-xl hover:shadow-2xl inline-flex items-center justify-center group"
                >
                  Our Story
                  <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
                </motion.a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">Our Achievements</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Numbers that speak to our commitment to excellence and our position as a leader in transportation services.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, threshold: 0.2 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {achievements.map((achievement, index) => (
              <motion.div
                key={index}
                variants={fadeIn}
                className="text-center bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <motion.div
                  className="w-20 h-20 bg-gradient-to-r from-[#1d526d] to-[#2a6b89] rounded-2xl flex items-center justify-center mx-auto mb-6"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <achievement.icon className="w-10 h-10 text-white" />
                </motion.div>
                <div className="text-4xl font-bold text-[#1d526d] mb-2">{achievement.title}</div>
                <div className="text-lg font-bold text-black mb-3">{achievement.subtitle}</div>
                <p className="text-gray-600 leading-relaxed">{achievement.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section id="story" className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              ref={storyRef}
              variants={slideIn}
              initial="hidden"
              animate={storyInView ? "visible" : "hidden"}
            >
              <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">Our Story</h2>
              <div className="space-y-6 text-gray-600 leading-relaxed text-lg">
                <p>{aboutContent.story}</p>
                <div className="grid grid-cols-2 gap-6 mt-8">
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <div className="text-3xl font-bold text-[#1d526d] mb-2">
                      <AnimatedCounter number={2006} suffix="" duration={2} />
                    </div>
                    <div className="text-gray-600 font-bold">Founded</div>
                  </div>
                  <div className="text-center p-6 bg-gray-50 rounded-xl">
                    <div className="text-3xl font-bold text-[#1d526d] mb-2">
                      <AnimatedCounter number={18} suffix="+" duration={2} />
                    </div>
                    <div className="text-gray-600 font-bold">Years Experience</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={storyInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={lepusTruck}
                  alt="Lepus Group Story" 
                  className="w-full h-96 lg:h-[500px] object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="bg-white/95 backdrop-blur-sm rounded-xl p-4">
                    <div className="flex items-center text-gray-700">
                      <Target className="w-5 h-5 text-[#1d526d] mr-3" />
                      <span className="font-bold">Transportation Excellence</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">Our Values</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              The core principles that guide everything we do at {companyInfo.name}, 
              ensuring we deliver exceptional service every time.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, threshold: 0.1 }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {aboutContent.values.map((value, index) => (
              <ValueCard key={index} value={value} index={index} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-black mb-6">Our Journey</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Key milestones that have shaped our growth and success over the years.
            </p>
          </motion.div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-4 w-1 h-full bg-gradient-to-b from-[#1d526d] to-[#2a6b89] lg:left-1/2 lg:-translate-x-1/2"></div>
            
            {aboutContent.milestones.map((milestone, index) => (
              <MilestoneCard key={index} milestone={milestone} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="py-16 lg:py-24 bg-[#1d526d] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={lepusTruck}
            alt="" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, threshold: 0.3 }}
              transition={{ duration: 0.6 }}
              className="text-center lg:text-left"
            >
              <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">Our Vision</h3>
              <p className="text-xl text-gray-200 leading-relaxed mb-8">
                {aboutContent.vision}
              </p>
              <div className="flex items-center justify-center lg:justify-start">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <span className="text-white font-bold">Strategic Excellence</span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, threshold: 0.3 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-center lg:text-left"
            >
              <h3 className="text-3xl lg:text-4xl font-bold text-white mb-6">Our Mission</h3>
              <p className="text-xl text-gray-200 leading-relaxed mb-8">
                {aboutContent.mission}
              </p>
              <div className="flex items-center justify-center lg:justify-start">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mr-4">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <span className="text-white font-bold">Committed to Excellence</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-black">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, threshold: 0.3 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">Ready to Experience Excellence?</h2>
            <p className="text-xl text-gray-300 mb-12 leading-relaxed max-w-4xl mx-auto">
              Join the thousands of satisfied clients who trust {companyInfo.name} with their transportation needs. 
              Experience our commitment to excellence firsthand.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link 
                  to="/contact"
                  className="bg-[#1d526d] hover:bg-white hover:text-black text-white px-10 py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center group shadow-xl"
                >
                  Contact Us Today
                  <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link 
                  to="/services"
                  className="border-2 border-[#1d526d] text-[#1d526d] px-10 py-4 rounded-xl font-bold text-lg hover:bg-[#1d526d] hover:text-white transition-all duration-300 flex items-center justify-center"
                >
                  Our Services
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;